import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { BrandProvider } from "./components/AppShell";
import AcceptInvite from "./pages/AcceptInvite";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import AppFooter from "./components/AppFooter";
import Dashboard from "./pages/Dashboard";
import Organizations from "./pages/Organizations";
import Tasks from "./pages/Tasks";
import Invoices from "./pages/Invoices";
import Accounting from "./pages/Accounting";
import Subjects from "./pages/Subjects";
import Messages from "./pages/Messages";
import Email from "./pages/Email";
import Cases from "./pages/Cases";
import Jobs from "./pages/Jobs";
import JobDash from "./pages/JobDash";
import Locations from "./pages/Locations";
import Calendar from "./pages/Calendar";
import RoutesPage from "./pages/Routes";
import Templates from "./pages/Templates";
import GLBA from "./pages/GLBA";
import Intake from "./pages/Intake";
import Settings from "./pages/Settings";
import SubmitJob from "./pages/SubmitJob";
import MyJobs from "./pages/MyJobs";
import MyInvoices from "./pages/MyInvoices";
import MyHosts from "./pages/MyHosts";
import UpgradeSuccess from "./pages/UpgradeSuccess";
import DevConsole, { ImpersonationBanner } from "./pages/DevConsole";
import AnnouncementBanner from "./components/AnnouncementBanner";
import CustomerSupport from "./pages/CustomerSupport";
import Bills from "./pages/Bills";
import MAccount from "./mobile/pages/MAccount";
import MJobs from "./mobile/pages/MJobs";
import MJobDetail from "./mobile/pages/MJobDetail";
import MAttemptForm from "./mobile/pages/MAttemptForm";
import MRoutes from "./mobile/pages/MRoutes";
import MRouteDetail from "./mobile/pages/MRouteDetail";
import MScan from "./mobile/pages/MScan";
import MProof from "./mobile/pages/MProof";
import MNotaryInbox from "./mobile/pages/MNotaryInbox";
import ProofsInbox from "./pages/ProofsInbox";
import MigrationInProgress from "./pages/MigrationInProgress";
import PublicMigrationStatus from "./pages/PublicMigrationStatus";
import CommandCenter from "./pages/CommandCenter";

// iter 73.15 — Shell layout hardened for full-viewport pages (Command
// Center, Dispatch Map, etc.). `<main>` is now an *overflow-hidden* flex
// column: the ImpersonationBanner wrapper takes intrinsic height, and
// the children slot fills the remaining space. Pages that want
// full-bleed just use `h-full`; pages that scroll inherit the inner
// container's auto scroll.
const Shell = ({ children }) => (
  <div className="flex flex-col min-h-screen" data-testid="app-shell">
    <TopBar />
    <AnnouncementBanner />
    <div className="flex flex-1 min-h-0">
      <Sidebar />
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden bg-background text-foreground">
        <div className="px-8 pt-4 flex-shrink-0"><ImpersonationBanner /></div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto" data-testid="app-main-scroll">
          {children}
        </div>
      </main>
    </div>
    <AppFooter />
  </div>
);

const LoadingScreen = () => {
  const [showReset, setShowReset] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowReset(true), 4000);
    return () => clearTimeout(t);
  }, []);
  const resetSession = () => {
    try { localStorage.clear(); } catch (_) { /* ignore */ }
    try { sessionStorage.clear(); } catch (_) { /* ignore */ }
    window.location.href = "/login";
  };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-muted-foreground gap-4" data-testid="loading-screen">
      <div className="text-sm">Loading…</div>
      {showReset && (
        <button
          onClick={resetSession}
          className="btn btn-secondary btn-sm"
          data-testid="reset-session-btn"
          title="Clears stored session and returns you to the sign-in page"
        >
          STUCK? RESET SESSION
        </button>
      )}
    </div>
  );
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  // iter 72 — gate: if our org is mid-merge, ship everyone to the
  // branded landing page so they don't accidentally edit data that
  // won't survive.  We check `user.active_migration_job_id` which the
  // auth layer hydrates from /api/unify/jobs/active-for-source/{org}.
  // If the field's missing (older auth payload) we skip the gate — the
  // landing page itself will show "no active migration" if accessed
  // directly.
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.active_migration_job_id && window.location.pathname !== "/migration-in-progress") {
    return <Navigate to="/migration-in-progress" replace />;
  }
  return <Shell>{children}</Shell>;
};

/** iter 42 — Mobile shell: auth-gated, but renders WITHOUT the desktop Shell
 *  (no Sidebar, no TopBar, no AppFooter). The MobileShell component owns its
 *  own top/bottom chrome. */
const MobileProtected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const NoContractor = ({ children }) => {
  const { user } = useAuth();
  if (user?.org_type === "contractor") {
    // Redirect contractors away from /intake — sidebar already hides the link
    // for them, but this guards direct URL bypass.
    return <Navigate to="/" replace />;
  }
  return children;
};

const Public = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return null;
  // Iter 32 follow-up: honour the user's `default_landing_page` preference
  // when bouncing off /login. Falls back to "/" when unset or invalid.
  // iter 42: process servers (role='server') are auto-routed to the mobile
  // driver app at /m/jobs unless they have an explicit landing-page override.
  if (user) {
    let target;
    if (typeof user.default_landing_page === "string" && user.default_landing_page.startsWith("/")) {
      target = user.default_landing_page;
    } else if (user.role === "server") {
      target = "/m/jobs";
    } else {
      target = "/";
    }
    return <Navigate to={target} replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <BrandProvider>
          <Routes>
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
            <Route path="/reset-password" element={<Public><ResetPassword /></Public>} />
            {/* iter 72 — Branded landing page for orgs mid-migration. NOT gated
                by Protected so users whose session was just killed can still see it. */}
            <Route path="/migration-in-progress" element={<MigrationInProgress />} />
            <Route path="/migration-status/:jobId" element={<PublicMigrationStatus />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/submit-job" element={<Protected><SubmitJob /></Protected>} />
            <Route path="/my-jobs" element={<Protected><MyJobs /></Protected>} />
            <Route path="/my-invoices" element={<Protected><MyInvoices /></Protected>} />
            <Route path="/my-hosts" element={<Protected><MyHosts /></Protected>} />
            <Route path="/upgrade-success" element={<Protected><UpgradeSuccess /></Protected>} />
            <Route path="/dev" element={<Protected><DevConsole /></Protected>} />
            <Route path="/support" element={<Protected><CustomerSupport /></Protected>} />
            <Route path="/payables" element={<Protected><Bills /></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/command-center" element={<Protected><CommandCenter /></Protected>} />
            <Route path="/organizations" element={<Protected><Organizations /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/receivables" element={<Protected><Invoices /></Protected>} />
            <Route path="/accounting" element={<Protected><Accounting /></Protected>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/email" element={<Protected><Email /></Protected>} />
            <Route path="/cases" element={<Protected><Cases /></Protected>} />
            <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
            <Route path="/job-dash/:id" element={<Protected><JobDash /></Protected>} />
            <Route path="/locations" element={<Protected><Locations /></Protected>} />
            <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
            <Route path="/routes" element={<Protected><RoutesPage /></Protected>} />
            <Route path="/subjects" element={<Protected><Subjects /></Protected>} />
            <Route path="/templates" element={<Protected><Templates /></Protected>} />
            <Route path="/glba" element={<Protected><GLBA /></Protected>} />
            <Route path="/intake" element={<Protected><NoContractor><Intake /></NoContractor></Protected>} />
            {/* iter 44 / 70 — /team and /notaries are gone — they were
                consolidated into Settings → My Organization. Old deep-links
                are kept as redirects so saved bookmarks don't 404. */}
            <Route path="/team" element={<Navigate to="/settings?tab=my-org" replace />} />
            <Route path="/notaries" element={<Navigate to="/settings?tab=my-org" replace />} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/proofs" element={<Protected><ProofsInbox /></Protected>} />

            {/* iter 42 — Mobile (driver) app routes */}
            <Route path="/m" element={<Navigate to="/m/jobs" replace />} />
            <Route path="/m/account" element={<MobileProtected><MAccount /></MobileProtected>} />
            <Route path="/m/jobs" element={<MobileProtected><MJobs /></MobileProtected>} />
            <Route path="/m/jobs/:id" element={<MobileProtected><MJobDetail /></MobileProtected>} />
            <Route path="/m/jobs/:job_id/attempt/new" element={<MobileProtected><MAttemptForm /></MobileProtected>} />
            <Route path="/m/attempts/:attempt_id" element={<MobileProtected><MAttemptForm /></MobileProtected>} />
            <Route path="/m/routes" element={<MobileProtected><MRoutes /></MobileProtected>} />
            <Route path="/m/routes/:id" element={<MobileProtected><MRouteDetail /></MobileProtected>} />
            <Route path="/m/scan" element={<MobileProtected><MScan /></MobileProtected>} />
            <Route path="/m/proof/:id" element={<MobileProtected><MProof /></MobileProtected>} />
            <Route path="/m/notary" element={<MobileProtected><MNotaryInbox /></MobileProtected>} />
          </Routes>
          </BrandProvider>
        </BrowserRouter>
      </AuthProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
